import React, { FC } from 'react';

import toLower from 'lodash/toLower';
import {
    BOLINDA_ICON_ASSISTANT,
    BOLINDA_ICON_LIBX,
    MATERIAL_ICON_CAMPAIGNS,
    MATERIAL_ICON_LANGUAGE
} from '../common/Button/Icon';
import LinkWithIconLeft from '../common/LinkWithIconLeft';
import { CAMPAIGN_PRODUCTS, COLLECTION_NAVIGATION_V2, useFeature } from '../utils/togglz';
import { LOANFORMAT_ALL } from '../../store/loanFormat/actions';
import AgeCategorySelector from '../common/AgeCategorySelector';
import { LinkContainer } from './NavigationLinksContainer';
import SubNavigationLayout from './SubNavigationLayout';
import { URL_FOREIGN_LANGUAGE_ROUTE, URL_LOTE_ROUTE } from '../../shop/language/constants';
import { URL_CAMPAIGNS_ROUTE, URL_UNLIMITED_ROUTE } from '../../shop/unlimited/constants';
import { NavigationLink } from './NavigationLink';
import { LoanFormatIcon } from '../common/GildIcon';
import { useTranslation } from 'react-i18next';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';

type Props = {
    readonly isSticky: boolean;
};

const ShopNavigationLegacy: FC<Props> = props => {
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);
    const campaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const { t } = useTranslation();
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();
    const searchParams = new URLSearchParams(ageCategorySearchParam ? { ageCategory: ageCategorySearchParam } : {});

    return (
        <SubNavigationLayout
            center={
                <LinkContainer>
                    <li>
                        <LinkWithIconLeft to={{ pathname: '/catalogue/eaudiobooks', search: searchParams.toString() }}>
                            <LoanFormatIcon aria-hidden={true} icon="eaudiobook-filled" />
                            <span>{t('menu.shop.eaudiobooksMain')}</span>
                        </LinkWithIconLeft>
                    </li>
                    <li>
                        <LinkWithIconLeft to={{ pathname: '/catalogue/ebooks', search: searchParams.toString() }}>
                            <LoanFormatIcon aria-hidden={true} icon="ebook-filled" />
                            <span>{t('menu.shop.ebooksMain')}</span>
                        </LinkWithIconLeft>
                    </li>
                    <li>
                        <LinkWithIconLeft to={{ pathname: '/catalogue/epress', search: searchParams.toString() }}>
                            <LoanFormatIcon aria-hidden={true} icon="emagazine-filled" />
                            <span>{t('menu.shop.epressMain')}</span>
                        </LinkWithIconLeft>
                    </li>
                    <li>
                        <LinkWithIconLeft
                            to={{
                                pathname: `/${URL_FOREIGN_LANGUAGE_ROUTE}/${URL_LOTE_ROUTE}/all`,
                                search: searchParams.toString()
                            }}
                        >
                            <i className="material-icons" aria-hidden={true}>
                                {MATERIAL_ICON_LANGUAGE}
                            </i>
                            <span>{t('menu.shop.languagesMain')}</span>
                        </LinkWithIconLeft>
                    </li>

                    {campaignsEnabled ? (
                        <li>
                            <LinkWithIconLeft
                                to={{
                                    pathname: `/${URL_UNLIMITED_ROUTE}/${URL_CAMPAIGNS_ROUTE}/${toLower(
                                        LOANFORMAT_ALL
                                    )}`,
                                    search: searchParams.toString()
                                }}
                            >
                                <i className="material-icons" aria-hidden={true}>
                                    {MATERIAL_ICON_CAMPAIGNS}
                                </i>
                                <span>{t('menu.shop.unlimitedMain')}</span>
                            </LinkWithIconLeft>
                        </li>
                    ) : null}
                    <li>
                        <NavigationLink
                            to={{ pathname: '/libxchange', search: searchParams.toString() }}
                            className="material-link nav-new"
                        >
                            <i className="bolinda-icons" aria-hidden={true}>
                                {BOLINDA_ICON_LIBX}
                            </i>
                            <span>
                                {t('menu.shop.libxchangeMain')}
                                {!campaignsEnabled && !props.isSticky && <span className="new-label">New</span>}
                            </span>
                        </NavigationLink>
                    </li>
                    <li>
                        <LinkWithIconLeft
                            to={{
                                pathname: isCollectionNavigationV2Enabled
                                    ? '/collectionmgmt/reserves'
                                    : '/assistant/reserves',
                                search: searchParams.toString()
                            }}
                            theme="warning"
                        >
                            <i className="bolinda-icons" aria-hidden={true}>
                                {BOLINDA_ICON_ASSISTANT}
                            </i>
                            <span>{t('menu.shop.orderingAssistantMain')}</span>
                        </LinkWithIconLeft>
                    </li>
                </LinkContainer>
            }
            right={!props.isSticky ? <AgeCategorySelector /> : null}
            isSticky={props.isSticky}
            width="15"
        />
    );
};

export default ShopNavigationLegacy;
